/**
 * AgentTargetsTab — Admin-only inline-editable table for per-agent daily targets:
 *   • Samples per day (sample_shared = Yes on any requirement)
 *   • Calls Attempted per day
 *   • LFB Created per day (requirements with lfb_id filled)
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Loader2, Target, Save, RotateCcw } from "lucide-react";

const AgentTargetsTab = () => {
  const [rows, setRows] = useState([]);
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agent-targets`);
      setRows(res.data || []);
      const map = {};
      (res.data || []).forEach((r) => {
        map[r.user_id] = {
          samples_per_day: r.samples_per_day,
          calls_per_day: r.calls_per_day,
          lfb_per_day: r.lfb_per_day,
        };
      });
      setOriginal(map);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load targets");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateField = (userId, field, value) => {
    const v = Math.max(0, parseInt(value, 10) || 0);
    setRows((rs) => rs.map((r) => r.user_id === userId ? { ...r, [field]: v } : r));
  };

  const isDirty = (row) => {
    const o = original[row.user_id] || {};
    return o.samples_per_day !== row.samples_per_day
        || o.calls_per_day !== row.calls_per_day
        || o.lfb_per_day !== row.lfb_per_day;
  };

  const saveRow = async (row) => {
    setSavingId(row.user_id);
    try {
      await axios.post(`${API}/agent-targets/${row.user_id}`, {
        samples_per_day: row.samples_per_day,
        calls_per_day: row.calls_per_day,
        lfb_per_day: row.lfb_per_day,
      });
      toast.success(`Targets saved for ${row.name}`);
      setOriginal((o) => ({ ...o, [row.user_id]: {
        samples_per_day: row.samples_per_day,
        calls_per_day: row.calls_per_day,
        lfb_per_day: row.lfb_per_day,
      } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSavingId(null); }
  };

  const resetRow = (row) => {
    const o = original[row.user_id] || {};
    updateField(row.user_id, "samples_per_day", o.samples_per_day || 0);
    updateField(row.user_id, "calls_per_day", o.calls_per_day || 0);
    updateField(row.user_id, "lfb_per_day", o.lfb_per_day || 0);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );

  return (
    <Card className="border-slate-200" data-testid="agent-targets-tab">
      <CardContent className="p-0">
        <div className="p-4 bg-blue-50/50 border-b border-blue-100 text-sm text-blue-800 flex items-start gap-2">
          <Target className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Per-Agent Daily Targets</p>
            <p className="text-xs text-blue-700 mt-0.5">Set how many <strong>Samples Shared</strong>, <strong>Calls Attempted</strong>, and <strong>LFBs Created</strong> each agent should hit per working day. Achievement % is computed live on the Dashboard & Performance views.</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="agent-targets-table">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase py-3 px-4">Agent</th>
                <th className="text-center text-xs font-semibold text-slate-600 uppercase py-3 px-4">Samples / day</th>
                <th className="text-center text-xs font-semibold text-slate-600 uppercase py-3 px-4">Calls / day</th>
                <th className="text-center text-xs font-semibold text-slate-600 uppercase py-3 px-4">LFB / day</th>
                <th className="text-right text-xs font-semibold text-slate-600 uppercase py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-slate-400 py-8">No agents found</td></tr>
              ) : rows.map((r) => {
                const dirty = isDirty(r);
                return (
                  <tr key={r.user_id} className={dirty ? "bg-amber-50/50" : "hover:bg-slate-50"} data-testid={`target-row-${r.user_id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.email}{r.role === "admin" ? " · Admin" : ""}</div>
                    </td>
                    <td className="py-3 px-4">
                      <Input
                        type="number" min={0}
                        value={r.samples_per_day}
                        onChange={(e) => updateField(r.user_id, "samples_per_day", e.target.value)}
                        className="w-20 mx-auto text-center"
                        data-testid={`samples-${r.user_id}`}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <Input
                        type="number" min={0}
                        value={r.calls_per_day}
                        onChange={(e) => updateField(r.user_id, "calls_per_day", e.target.value)}
                        className="w-20 mx-auto text-center"
                        data-testid={`calls-${r.user_id}`}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <Input
                        type="number" min={0}
                        value={r.lfb_per_day}
                        onChange={(e) => updateField(r.user_id, "lfb_per_day", e.target.value)}
                        className="w-20 mx-auto text-center"
                        data-testid={`lfb-${r.user_id}`}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        {dirty && (
                          <Button size="sm" variant="ghost" onClick={() => resetRow(r)} className="text-slate-500" title="Discard changes">
                            <RotateCcw className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => saveRow(r)}
                          disabled={!dirty || savingId === r.user_id}
                          className={dirty ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-300"}
                          data-testid={`save-target-${r.user_id}`}
                        >
                          {savingId === r.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AgentTargetsTab;
